import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  EventNote,
  CheckCircle,
  Cancel,
  Schedule,
  LocationOn,
  AttachMoney,
  ArrowBack,
  Refresh,
  CalendarToday,
  Close,
  Person,
  HourglassEmpty,
  ThumbUp,
  ThumbDown,
  Payments,
  Warning
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Librarian Bookings - View and manage all library bookings
 */
function LibrarianBookings({ showSnackbar }) {
  const navigate = useNavigate();

  const [library, setLibrary] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [upcomingFromApi, setUpcomingFromApi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [declineDialog, setDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchBookingsData();
  }, []);

  const fetchBookingsData = async () => {
    try {
      setLoading(true);

      // Fetch librarian's assigned library (includes booking_auto_approve flag)
      const libraryResponse = await api.get('/librarian/library');

      if (!libraryResponse.data.data.library) {
        showSnackbar('No library assigned', 'error');
        setLoading(false);
        return;
      }

      const myLibrary = libraryResponse.data.data.library;
      setLibrary(myLibrary);

      // Fetch all bookings for this library
      const bookingsResponse = await api.get('/bookings', {
        params: { library_id: myLibrary.id }
      });
      setAllBookings(bookingsResponse.data.data.bookings || []);

      // Fetch upcoming bookings from the precise server-side endpoint
      try {
        const upcomingResponse = await api.get('/librarian/bookings/upcoming', {
          params: { page: 1, limit: 100 }
        });
        setUpcomingFromApi(upcomingResponse.data.data.bookings || []);
      } catch (err) {
        console.error('Error fetching upcoming bookings:', err);
        showSnackbar(
          err.response?.data?.message || 'Failed to load upcoming bookings',
          'error'
        );
        setUpcomingFromApi([]);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      showSnackbar('Failed to load bookings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle />;
      case 'upcoming':
        return <Schedule />;
      case 'completed':
        return <CheckCircle />;
      case 'cancelled':
        return <Cancel />;
      case 'pending':
        return <HourglassEmpty />;
      case 'declined':
        return <ThumbDown />;
      default:
        return <EventNote />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'upcoming':
        return 'primary';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      setActionLoading(true);
      await api.post(`/librarian/bookings/${bookingId}/accept`);
      showSnackbar('Booking accepted successfully', 'success');
      setDetailDialog(false);
      fetchBookingsData();
    } catch (err) {
      console.error('Error accepting booking:', err);
      showSnackbar(err.response?.data?.message || 'Failed to accept booking', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineBooking = async () => {
    try {
      setActionLoading(true);
      await api.post(`/librarian/bookings/${selectedBooking.id}/decline`, {
        reason: declineReason || 'No reason provided'
      });
      showSnackbar('Booking declined successfully', 'success');
      setDeclineDialog(false);
      setDetailDialog(false);
      setDeclineReason('');
      fetchBookingsData();
    } catch (err) {
      console.error('Error declining booking:', err);
      showSnackbar(err.response?.data?.message || 'Failed to decline booking', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPayment = async (bookingId) => {
    try {
      setActionLoading(true);
      await api.post(`/librarian/bookings/${bookingId}/confirm-payment`);
      showSnackbar('Payment confirmed successfully. Student can now check in.', 'success');
      fetchBookingsData();
    } catch (err) {
      console.error('Error confirming payment:', err);
      showSnackbar(err.response?.data?.message || 'Failed to confirm payment', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const filterBookings = (status) => {
    switch (status) {
      case 'pending':
        return allBookings.filter(b => b.status === 'pending');
      case 'active':
        return allBookings.filter(b => b.status === 'active');
      case 'past':
        return allBookings.filter(b => b.status === 'completed' || b.status === 'cancelled' || b.status === 'declined');
      default:
        return allBookings;
    }
  };

  const pendingBookings = filterBookings('pending');
  const activeBookings = filterBookings('active');
  // upcomingBookings comes from the dedicated server-side endpoint for precision
  const upcomingBookings = upcomingFromApi;
  const pastBookings = filterBookings('past');

  // Derived from the library object returned by /librarian/library
  const autoApproveEnabled = library?.booking_auto_approve === true;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const getCurrentBookings = () => {
    switch (currentTab) {
      case 0:
        return pendingBookings;
      case 1:
        return allBookings;
      case 2:
        return activeBookings;
      case 3:
        // Sourced from /librarian/bookings/upcoming — server-computed, date-precise
        return upcomingFromApi;
      case 4:
        return pastBookings;
      default:
        return allBookings;
    }
  };

  /**
   * Renders a human-readable label for days_until_start.
   * 0 → "Today", 1 → "Tomorrow", N → "in N days"
   */
  const formatDaysUntilStart = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `in ${days} days`;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading bookings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <IconButton
                onClick={() => navigate('/librarian/manage')}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Box flex={1}>
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Bookings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {library?.name || 'Library'} - All Bookings
                </Typography>
              </Box>
              <IconButton
                onClick={fetchBookingsData}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Stats */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 2,
              background: GRADIENTS.brand,
              color: 'white'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-around" textAlign="center">
                <Box>
                  <Badge
                    badgeContent={autoApproveEnabled ? 0 : pendingBookings.length}
                    color="error"
                    max={99}
                  >
                    <Typography variant="h3" fontWeight="800">
                      {pendingBookings.length}
                    </Typography>
                  </Badge>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Pending
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                <Box>
                  <Typography variant="h3" fontWeight="800">
                    {activeBookings.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Active
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                <Box>
                  <Typography variant="h3" fontWeight="800">
                    {upcomingBookings.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Upcoming
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                <Box>
                  <Typography variant="h3" fontWeight="800">
                    {allBookings.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Total
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </MotionCard>

          {/* Auto-approve info banner */}
          {autoApproveEnabled && (
            <MotionBox variants={itemVariants} mb={2}>
              <Alert severity="info">
                Auto-approval is enabled. New bookings (when paid via wallet) confirm automatically and skip the Pending tab. Pay-at-library bookings still appear here.
              </Alert>
            </MotionBox>
          )}

          {/* Tabs */}
          <MotionBox variants={itemVariants} mb={2}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                bgcolor: 'background.paper',
                borderRadius: RADIUS.lg,
                p: 0.5,
                '& .MuiTabs-indicator': {
                  display: 'none'
                },
                '& .MuiTab-root': {
                  borderRadius: RADIUS.md,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  minHeight: 48,
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    background: GRADIENTS.brand,
                    color: 'white'
                  }
                }
              }}
            >
              <Tab
                label={
                  <Badge
                    badgeContent={autoApproveEnabled ? 0 : pendingBookings.length}
                    color="error"
                    max={99}
                  >
                    <span>Pending</span>
                  </Badge>
                }
              />
              <Tab label={`All (${allBookings.length})`} />
              <Tab label={`Active (${activeBookings.length})`} />
              <Tab label={`Upcoming (${upcomingBookings.length})`} />
              <Tab label={`Past (${pastBookings.length})`} />
            </Tabs>
          </MotionBox>

          {/* Content */}
          <AnimatePresence mode="wait">
            <MotionCard
              key={currentTab}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              sx={{ borderRadius: RADIUS.xl }}
            >
              <CardContent sx={{ p: 0 }}>
                {getCurrentBookings().length === 0 ? (
                  <Box p={6} textAlign="center">
                    <EventNote sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Bookings Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentTab === 0 && 'No pending bookings awaiting approval'}
                      {currentTab === 1 && 'No bookings for this library yet'}
                      {currentTab === 2 && 'No active bookings at the moment'}
                      {currentTab === 3 && 'No upcoming bookings'}
                      {currentTab === 4 && 'No past bookings'}
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {getCurrentBookings().map((booking, index) => (
                      <React.Fragment key={booking.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 2,
                            px: 2,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                          onClick={() => {
                            setSelectedBooking(booking);
                            setDetailDialog(true);
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                width: 50,
                                height: 50,
                                background: GRADIENTS.brand,
                                fontWeight: 700
                              }}
                            >
                              {booking.student?.name?.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography fontWeight="700" variant="body1">
                                {booking.student?.name || 'Unknown Student'}
                              </Typography>
                            }
                            secondary={
                              <Box mt={0.5}>
                                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                  <EventNote fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {booking.plan?.name || 'N/A'}
                                  </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <CalendarToday fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(booking.start_date).toLocaleDateString('en-IN', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })} - {new Date(booking.end_date).toLocaleDateString('en-IN', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                            <Chip
                              label={booking.status.replace('_', ' ').toUpperCase()}
                              color={getStatusColor(booking.status)}
                              size="small"
                              icon={getStatusIcon(booking.status)}
                              sx={{ fontWeight: 600 }}
                            />
                            {currentTab === 3 && booking.days_until_start != null && (
                              <Chip
                                label={formatDaysUntilStart(booking.days_until_start)}
                                size="small"
                                color={booking.days_until_start === 0 ? 'error' : booking.days_until_start === 1 ? 'warning' : 'default'}
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </MotionCard>
          </AnimatePresence>
        </MotionBox>
      </Container>

      {/* Booking Detail Dialog */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        {selectedBooking && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight="700">Booking Details</Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{selectedBooking.id.substring(0, 8)}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDetailDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              {/* Student Info */}
              <Box sx={{ bgcolor: 'action.hover', borderRadius: RADIUS.lg, p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      background: GRADIENTS.brand,
                      fontWeight: 700
                    }}
                  >
                    {selectedBooking.student?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="700">
                      {selectedBooking.student?.name || 'Unknown Student'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedBooking.student?.email || 'No email'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Status */}
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Status
                </Typography>
                <Chip
                  icon={getStatusIcon(selectedBooking.status)}
                  label={selectedBooking.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(selectedBooking.status)}
                  size="medium"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* Payment Status - Show if pending_payment */}
              {selectedBooking.payment_status === 'pending_payment' && (
                <Alert
                  severity="warning"
                  icon={<Payments />}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => handleConfirmPayment(selectedBooking.id)}
                      disabled={actionLoading}
                      sx={{ fontWeight: 600 }}
                    >
                      {actionLoading ? 'Confirming...' : 'Confirm Payment'}
                    </Button>
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" fontWeight="600">
                    Pay at Library - Awaiting Payment
                  </Typography>
                  <Typography variant="caption">
                    Student needs to pay ₹{selectedBooking.amount_paid ? Math.round(selectedBooking.amount_paid / 100) : selectedBooking.plan?.price_cents} at counter
                  </Typography>
                </Alert>
              )}

              {selectedBooking.payment_status === 'paid' && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="600">
                    Payment Confirmed
                  </Typography>
                </Alert>
              )}

              {/* Plan Details */}
              {selectedBooking.plan && (
                <Box
                  sx={{
                    background: GRADIENTS.brand,
                    color: 'white',
                    borderRadius: RADIUS.lg,
                    p: 2,
                    mb: 2
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9 }} display="block" mb={0.5}>
                    Plan
                  </Typography>
                  <Typography variant="h6" fontWeight="700" gutterBottom>
                    {selectedBooking.plan.name}
                  </Typography>
                  <Box display="flex" gap={3}>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }} display="block">
                        {selectedBooking.amount_paid && Math.round(selectedBooking.amount_paid / 100) < selectedBooking.plan.price_cents ? 'Amount Paid (Discounted)' : 'Price'}
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        ₹{selectedBooking.amount_paid ? Math.round(selectedBooking.amount_paid / 100) : selectedBooking.plan.price_cents}
                      </Typography>
                      {selectedBooking.amount_paid && Math.round(selectedBooking.amount_paid / 100) < selectedBooking.plan.price_cents && (
                        <Typography variant="caption" sx={{ opacity: 0.7, textDecoration: 'line-through' }}>
                          ₹{selectedBooking.plan.price_cents}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }} display="block">
                        Duration
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {selectedBooking.plan.duration_days} days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }} display="block">
                        Seats
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {selectedBooking.plan.seats_included}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Booking Period */}
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: RADIUS.md, p: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Start Date
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {new Date(selectedBooking.start_date).toLocaleDateString('en-IN', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: RADIUS.md, p: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      End Date
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {new Date(selectedBooking.end_date).toLocaleDateString('en-IN', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Created At */}
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Booked On
                </Typography>
                <Typography variant="body2">
                  {new Date(selectedBooking.created_at).toLocaleString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
              {selectedBooking.status === 'pending' && (
                <Box display="flex" gap={1} width="100%">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ThumbUp />}
                    onClick={() => handleAcceptBooking(selectedBooking.id)}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Accept
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<ThumbDown />}
                    onClick={() => setDeclineDialog(true)}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Decline
                  </Button>
                </Box>
              )}
              {selectedBooking.status === 'declined' && selectedBooking.decline_reason && (
                <Alert severity="error" sx={{ width: '100%', mb: 1 }}>
                  <Typography variant="caption" fontWeight="600">Decline Reason:</Typography>
                  <Typography variant="body2">{selectedBooking.decline_reason}</Typography>
                </Alert>
              )}
              <Button variant="outlined" onClick={() => setDetailDialog(false)} fullWidth>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Decline Reason Dialog */}
      <Dialog
        open={declineDialog}
        onClose={() => setDeclineDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="700">Decline Booking</Typography>
            <IconButton onClick={() => setDeclineDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for declining this booking. The student will be notified
            and any payment will be refunded to their wallet.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for declining"
            placeholder="e.g., Library is at full capacity, booking dates not available..."
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDeclineDialog(false);
              setDeclineReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ThumbDown />}
            onClick={handleDeclineBooking}
            disabled={actionLoading}
          >
            Decline Booking
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LibrarianBookings;
